import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import "../css/customercare.css";

function Customercare() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [supportMobile, setSupportMobile] = useState("");
  const [supportWebsite, setWebsite] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(
          doc(db, "app-settings", "app-settings")
        );
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setLogoUrl(data.companyLogo || "");
          setSupportEmail(data.supportEmail || "");
          setSupportMobile(data.supportMobile || "");
          setWebsite(data.website);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await setDoc(doc(db, "customer-care", formData.mobileNumber), {
        name: formData.name,
        mobileNumber: formData.mobileNumber,
        message: formData.message,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    if (supportMobile) {
      window.location.href = `tel:${supportMobile}`;
    }
  };

  const handleEmail = () => {
    if (supportEmail) {
      window.location.href = `mailto:${supportEmail}`;
    }
  };

  const handleWebsite = () => {
    if (!supportWebsite) return;

    const url = supportWebsite.startsWith("http")
      ? supportWebsite
      : `https://${supportWebsite}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleBackToWarranty = () => {
    navigate("/");
  };

  return (
    <div className="customercare-container">
      <div className="customercare-content">
        {/* Logo Section */}
        {logoUrl && (
          <div className="care-logo-section">
            <img src={logoUrl} alt="Company Logo" className="care-logo" />
          </div>
        )}

        <div className="care-header">
          <h1 className="care-title">Customer Care</h1>
          <p className="care-subtitle">
            We're here to help! Send us a message or contact us directly
          </p>
        </div>

        {!submitted ? (
          <div className="care-form-wrapper">
            {/* Contact Form Card */}
            <div className="care-form-card">
              <h2 className="form-card-title">Send us a Message</h2>
              <p className="form-card-subtitle">
                Fill out the form below and we'll get back to you soon
              </p>

              <form onSubmit={handleSubmit} className="care-form">
                <div className="care-form-group">
                  <label className="care-label">Name</label>
                  <input
                    className="care-input"
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="care-form-group">
                  <label className="care-label">Mobile Number</label>
                  <input
                    className="care-input"
                    type="tel"
                    name="mobileNumber"
                    placeholder="Enter your mobile number"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    pattern="[0-9]{10}"
                    required
                  />
                </div>

                <div className="care-form-group">
                  <label className="care-label">Message</label>
                  <textarea
                    className="care-textarea"
                    name="message"
                    placeholder="Tell us how we can help you..."
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="care-submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="care-loading-spinner"></span>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </form>
            </div>

            {/* Contact Details Card */}
            <div className="contact-details-card">
              <h2 className="form-card-title">Contact Details</h2>
              <p className="form-card-subtitle">
                Or reach out to us directly through these channels
              </p>

              <div className="contact-buttons">
                {supportMobile && (
                  <button
                    onClick={handleCall}
                    className="contact-button call-button"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <div className="contact-button-content">
                      <span className="contact-button-label">Call Us</span>
                      <span className="contact-button-value">
                        {supportMobile}
                      </span>
                    </div>
                  </button>
                )}

                {supportEmail && (
                  <button
                    onClick={handleEmail}
                    className="contact-button email-button"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <div className="contact-button-content">
                      <span className="contact-button-label">Email Us</span>
                      <span className="contact-button-value">
                        {supportEmail}
                      </span>
                    </div>
                  </button>
                )}

                {supportWebsite && (
                  <button
                    onClick={handleWebsite}
                    className="contact-button email-button"
                  >
                    {/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg> */}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      {" "}
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                      /> <path d="M2 12h20" />{" "}
                      <path d="M12 2c2.5 3 4 6.5 4 10s-1.5 7-4 10" />{" "}
                      <path d="M12 2c-2.5 3-4 6.5-4 10s1.5 7 4 10" />{" "}
                    </svg>
                    <div className="contact-button-content">
                      <span className="contact-button-label">Website</span>
                      <span className="contact-button-value">
                        {supportWebsite}
                      </span>
                    </div>
                  </button>
                )}
              </div>

              {/* Back to Warranty Form Button */}
              <button
                onClick={handleBackToWarranty}
                className="care-back-to-warranty-button"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Go Back to Warranty Form
              </button>
            </div>
          </div>
        ) : (
          <div className="care-success-card">
            <div className="care-success-icon">
              <svg width="70" height="70" viewBox="0 0 70 70" fill="none">
                <circle cx="35" cy="35" r="35" fill="#10B981" />
                <path
                  d="M20 35L30 45L50 25"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="care-success-title">
              Request Submitted Successfully!
            </h2>
            <p className="care-success-message">
              Thank you for contacting us. Our team will get back to you
              shortly.
            </p>

            {/* Contact Details in Success View */}
            <div className="success-contact-section">
              <p className="success-contact-text">Need immediate assistance?</p>
              <div className="success-contact-buttons">
                {supportMobile && (
                  <button onClick={handleCall} className="success-contact-btn">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    Call Now
                  </button>
                )}
                {supportEmail && (
                  <button onClick={handleEmail} className="success-contact-btn">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    Send Email
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: "", mobileNumber: "", message: "" });
              }}
              className="care-back-button"
            >
              Submit Another Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Customercare;
